import { ActionConfirmationHost } from "../ui/ActionConfirmation.js";
import { useState, type ReactNode } from "react";
import { get, type MyStudentDetail } from "../api.js";
import type { FamilySetupData } from "../familySetupApi.js";
import { formatDate, formatTime } from "../format.js";
import { useAsync } from "../useAsync.js";
import { FamilyWaitlistOffers } from "../waitlist/FamilyWaitlist.js";
import {
  booking,
  type AppointmentEnrichment,
  type StudentLesson,
  type StudentLessonEnrichment,
  type StudentLessonSchedule,
  type StudentLessonScheduleRow,
  type StudentLessons,
} from "./api.js";
import { ChangeLesson } from "./ChangeLesson.js";
import type { ChangeMode } from "./ChangeLesson.js";
import {
  familyPurchaseClaimReadState,
  HOME_PAST_ROWS,
  HOME_UPCOMING_ROWS,
  studentCreditFundingState,
  studentHasEligibleOnSiteRequest,
  StudentDashboardView,
} from "./StudentDashboard.js";

function numericBookingIds(lessons: StudentLessonScheduleRow[]): number[] {
  return [...new Set(lessons.map((lesson) => Number(lesson.bookingId)))].filter(
    (id) => Number.isInteger(id) && id > 0,
  );
}

/**
 * The booking ids Home enriches, split so the Next lesson is not held back.
 *
 * Each id costs FastAPI an Amelia read, so Home asks only for the rows it
 * renders, and asks for the Next lesson on its own: its payment and Move/Cancel
 * actions then arrive after one read instead of after the whole page's.
 */
export function homeEnrichmentBatches(schedule: StudentLessonSchedule): {
  next: number[];
  rest: number[];
} {
  const next = numericBookingIds(schedule.upcoming.slice(0, 1));
  const rest = numericBookingIds([
    ...schedule.upcoming.slice(1, HOME_UPCOMING_ROWS),
    ...schedule.past.slice(0, HOME_PAST_ROWS),
  ]).filter((id) => !next.includes(id));
  return { next, rest };
}

/** One enrichment request Home has sent, and how it has answered so far. */
export interface LessonEnrichmentBatch {
  bookingIds: readonly number[];
  enrichment: StudentLessonEnrichment | null;
  error: string | null;
}

const ENRICHMENT_LOAD_FAILED =
  "Booking details could not be loaded. Your lesson times are still available.";

function batchFailed(batch: LessonEnrichmentBatch): boolean {
  return batch.error !== null || batch.enrichment?.state === "unavailable";
}

/**
 * Lay each batch's answer over the mirror schedule.
 *
 * A failed batch makes only its own rows unavailable, so a slow or failed
 * request for the rest of Home cannot take away the Next lesson's actions once
 * they have arrived, nor the other way round. The page-wide notice is kept for
 * when every batch has failed — which is what a student-wide answer such as
 * "not linked" or "booking switched off" produces, since each batch hears it.
 */
export function mergeStudentLessonReads(
  schedule: StudentLessonSchedule,
  batches: readonly LessonEnrichmentBatch[],
): StudentLessons {
  const enriched = new Map<number, AppointmentEnrichment>();
  const unavailable = new Set<number>();
  for (const batch of batches) {
    for (const row of batch.enrichment?.appointments ?? []) enriched.set(row.booking_id, row);
    for (const id of batch.enrichment?.unavailableBookingIds ?? []) unavailable.add(id);
    if (batchFailed(batch)) for (const id of batch.bookingIds) unavailable.add(id);
  }

  const merge = (lesson: StudentLessonScheduleRow): StudentLesson => ({
    ...lesson,
    enrichment: enriched.get(Number(lesson.bookingId)) ?? null,
    unavailable: unavailable.has(Number(lesson.bookingId)),
  });

  const failed = batches.filter(batchFailed);
  const firstFailure = failed[0];
  const state: StudentLessons["enrichment"] =
    firstFailure && failed.length === batches.length
      ? firstFailure.error !== null
        ? { state: "unavailable", message: ENRICHMENT_LOAD_FAILED }
        : {
            state: "unavailable",
            ...(firstFailure.enrichment?.code ? { code: firstFailure.enrichment.code } : {}),
            ...(firstFailure.enrichment?.message
              ? { message: firstFailure.enrichment.message }
              : {}),
          }
      : batches.some((batch) => batch.enrichment?.state === "ok")
        ? { state: "ok" }
        : { state: "pending" };

  return {
    student: schedule.student,
    upcoming: schedule.upcoming.map(merge),
    past: schedule.past.map(merge),
    enrichment: state,
  };
}

type SchoolUpcomingLesson = MyStudentDetail["upcomingBookings"][number] & {
  domain?: "private" | "school";
  contextLabel?: string;
  canManageBooking?: boolean;
};

/**
 * A dual-context learner still uses the private booking dashboard, but School
 * sessions are not bookings and must not disappear behind that capability.
 * They are deliberately rendered read-only and carry no cancel/change action.
 */
function SchoolUpcoming({ lessons }: { lessons: SchoolUpcomingLesson[] }) {
  if (lessons.length === 0) return null;
  return (
    <section aria-labelledby="school-upcoming-heading">
      <h2 id="school-upcoming-heading">School lessons</h2>
      <div className="panel">
        {lessons.map((lesson) => (
          <div className="slot" key={lesson.appointmentId}>
            <time>{formatTime(lesson.startsAt)}</time>
            <div>
              <div>{formatDate(lesson.startsAt)}</div>
              <div className="small muted">
                {lesson.serviceName ?? lesson.contextLabel ?? "School lesson"}
                {lesson.tutorName && ` · ${lesson.tutorName}`}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Home's latency-isolated booking dashboard.
 *
 * Mirror lesson times and credits begin immediately. The policy/payment
 * batches follow as soon as the local booking ids are known and enrich the
 * already visible rows in place — the Next lesson in a request of its own — so
 * a slow booking backend can no longer hold the schedule behind its 120-second
 * request ceiling, nor the Next lesson behind the rest. Canonical School
 * lessons are loaded separately from the family learning read so booking
 * capability remains record-specific rather than being inferred from the
 * learner as a whole.
 */
export function FastStudentDashboard({
  studentId,
  learning,
  afterPrimaryAction,
  afterNextLesson,
  afterCredits,
}: {
  studentId: string;
  /** FamilyHome shares its bounded overview read so booking Home does not refetch every note. */
  learning?: {
    data: MyStudentDetail | null;
    error: string | null;
    loading: boolean;
    reload: () => void;
  };
  afterPrimaryAction?: ReactNode;
  afterNextLesson?: ReactNode;
  afterCredits?: ReactNode;
}) {
  return (
    <ActionConfirmationHost key={studentId}>
      <FastStudentDashboardContent
        studentId={studentId}
        learning={learning}
        afterPrimaryAction={afterPrimaryAction}
        afterNextLesson={afterNextLesson}
        afterCredits={afterCredits}
      />
    </ActionConfirmationHost>
  );
}

function FastStudentDashboardContent({
  studentId,
  learning: sharedLearning,
  afterPrimaryAction,
  afterNextLesson,
  afterCredits,
}: {
  studentId: string;
  learning?: {
    data: MyStudentDetail | null;
    error: string | null;
    loading: boolean;
    reload: () => void;
  };
  afterPrimaryAction?: ReactNode;
  afterNextLesson?: ReactNode;
  afterCredits?: ReactNode;
}) {
  const schedule = useAsync(() => booking.lessonSchedule(studentId), [studentId]);
  const credits = useAsync(() => booking.credits(studentId), [studentId]);
  const purchaseSetup = useAsync(() => get<FamilySetupData>("/family-setup"), [studentId]);
  const purchaseClaimState = familyPurchaseClaimReadState(
    purchaseSetup.data,
    purchaseSetup.loading,
    purchaseSetup.error,
  );
  const creditBalanceVerified = credits.data !== null && !credits.loading && credits.error === null;
  const onSiteOfferingsNeeded = studentCreditFundingState(
    studentId,
    credits.error === null ? credits.data : null,
    creditBalanceVerified,
    purchaseClaimState,
  ) === "zero";
  const onSiteOfferings = useAsync(
    () => onSiteOfferingsNeeded ? booking.offerings(studentId) : Promise.resolve(null),
    [studentId, onSiteOfferingsNeeded],
  );
  const onSiteRequestAvailable = onSiteOfferingsNeeded &&
    !onSiteOfferings.loading &&
    onSiteOfferings.error === null &&
    studentHasEligibleOnSiteRequest(studentId, onSiteOfferings.data);
  const learningFallback = useAsync<MyStudentDetail | null>(
    () => (sharedLearning
      ? Promise.resolve(null)
      : get<MyStudentDetail>(`/me/students/${encodeURIComponent(studentId)}`)),
    [studentId, Boolean(sharedLearning)],
  );
  const learning = sharedLearning ?? learningFallback;
  const batches = schedule.data ? homeEnrichmentBatches(schedule.data) : null;
  // Always ask for the Next lesson's batch once the schedule is known, even
  // when it is empty: the answer also says whether booking is available at all.
  const nextEnrichment = useAsync<StudentLessonEnrichment | null>(
    () => (batches ? booking.lessonEnrichment(studentId, batches.next) : Promise.resolve(null)),
    [studentId, batches?.next.join(",") ?? null],
  );
  const restEnrichment = useAsync<StudentLessonEnrichment | null>(
    () =>
      batches && batches.rest.length > 0
        ? booking.lessonEnrichment(studentId, batches.rest)
        : Promise.resolve(null),
    [studentId, batches?.rest.join(",") ?? null],
  );
  const [changing, setChanging] = useState<{
    bookingId: string;
    mode: ChangeMode;
  } | null>(null);

  const reloadAll = () => {
    schedule.reload();
    credits.reload();
    onSiteOfferings.reload();
    nextEnrichment.reload();
    restEnrichment.reload();
    learning.reload();
  };

  if (!schedule.data) {
    return (
      <div className="student-home-columns">
        <div className="student-home-column-main">
          {schedule.loading ? (
            <p className="empty">Loading your lessons…</p>
          ) : (
            <div className="error">
              We could not load your lesson dashboard right now. Please refresh and try again.
            </div>
          )}
          {afterNextLesson}
          {afterPrimaryAction}
        </div>
        <aside className="student-home-column-side">{afterCredits}</aside>
      </div>
    );
  }

  const { next, rest } = homeEnrichmentBatches(schedule.data);
  const data = mergeStudentLessonReads(schedule.data, [
    { bookingIds: next, enrichment: nextEnrichment.data, error: nextEnrichment.error },
    // Nothing is sent for an empty rest, so it says nothing about the page.
    ...(rest.length > 0
      ? [{ bookingIds: rest, enrichment: restEnrichment.data, error: restEnrichment.error }]
      : []),
  ]);
  const schoolLessons = ((learning.data?.upcomingBookings ?? []) as SchoolUpcomingLesson[]).filter(
    (lesson) => lesson.domain === "school" || lesson.canManageBooking === false,
  );

  return (
    <>
      <StudentDashboardView
        studentId={studentId}
        data={data}
        credits={credits.data}
        creditsLoading={credits.loading}
        creditsError={credits.error}
        purchaseClaimState={purchaseClaimState}
        onSiteRequestAvailable={onSiteRequestAvailable}
        changingLesson={changing}
        onManageLesson={(lesson, mode) =>
          setChanging((current) =>
            current?.bookingId === lesson.bookingId && current.mode === mode
              ? null
              : { bookingId: lesson.bookingId, mode },
          )
        }
        renderChangeLesson={(lesson, open, mode) =>
          open && mode ? (
            <ChangeLesson
              key={`${lesson.bookingId}:${mode}`}
              studentId={studentId}
              lesson={lesson}
              onDone={reloadAll}
              onClose={() => setChanging(null)}
              initialMode={mode}
            />
          ) : null
        }
        urgentAlerts={
          <FamilyWaitlistOffers key={studentId} studentId={studentId} onAccepted={reloadAll} />
        }
        afterNextLesson={afterNextLesson}
        afterPrimaryAction={afterPrimaryAction}
        afterCredits={afterCredits}
        afterUpcoming={
          <>
            {learning.error && (
              <div className="notice small">
                Private booking is available, but School lesson times could not be loaded right now.
              </div>
            )}
            <SchoolUpcoming lessons={schoolLessons} />
          </>
        }
      />
    </>
  );
}
